import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'

import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../constants/routes'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import MarkdownWrapper from '../utils/markdown-wrapper'
import ThreeFacts from './components/three-facts'

import { createSelector } from '@reduxjs/toolkit'
import { Link } from 'react-router-dom'
import Breadcrumb from '../elements/breadcrumb'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/underline-01.svg'
import anime from 'animejs'
import { Helmet } from 'react-helmet'
import MetaracismQuote from '../content/bands/metaracism'

const selectActiveUrl = state => state.nav.activeUrl
const selectKeyIssueShorts = state => state.keyIssueShort

const makeKeyIssue = createSelector(
    [selectActiveUrl, selectKeyIssueShorts],
    (activeUrl, keyIssueShorts) => {
        return keyIssueShorts.find(ki => ki.alias === activeUrl)
    }
)

const mapStateToProps = (state, ownProps) => {
    const keyIssue = makeKeyIssue(state)
    return {
        keyIssue,
        keyIssueCategories: state.keyIssueCategories,
        navState: state.nav
    }
}

const KeyIssueOverview = ({ dispatch, navState, keyIssue, keyIssueCategories }) => {
    const componentName = 'Key Issue Overview'
    const activeUrl = navState.activeUrl.substring(1)
    const color = keyIssueCategories?.find(c => c.name === keyIssue?.category)?.color

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            // Turn the nav white if it isnt already and our theme is blue or orange
            if (navState.theme !== 'white' && ['blue', 'magenta', 'teal'].includes(color)) {
                dispatch(saveNavTheme('white'))
            } else {
                // Otherwise make sure it is grey
                dispatch(saveNavTheme('grey'))
            }
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (keyIssue) {
            dispatch(fetchApiData(apiResourceEndpoint(activeUrl, 'LIST')))
        }
    }, [activeUrl])

    const handlePreloadingState = async () => {
        // TODO: We may have the small version of the key issue already loaded but not the full one
        // if (!keyIssue) {
        dispatch(fetchApiData(apiResourceEndpoint(activeUrl, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
        // } else {
        //     dispatch(completePreloading())
        // }
    }

    const handleAnimatingInState = async () => {
        const markerLine = animateLine()
        markerLine.finished.then(() => {
            dispatch(completeAnimatingIn())
        })
    }

    const handleAnimatingOutState = () => {
        dispatch(completeAnimatingOut())
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    if (!keyIssue) {
        return null
    }

    const animateLine = () => {
        return anime({
            targets: '.title-stroke #marker',
            strokeDashoffset: 0,
            easing: 'easeInQuart',
            duration: 750
        })
    }

    const textBlock = keyIssue?.overviewContent || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi nec odio in felis vehicula ultricies. Donec in purus nec erat ultricies ultrices. Nullam nec sapien vitae purus aliquam condimentum.'

    const { pageTitle, metaDescription, metaImage, category, title, primaryImage, contentBands, metaracismQuote, slug } = keyIssue

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={metaImage?.url} />
                <meta property="og:primaryImage" content={primaryImage?.url} />
            </Helmet>
            <Breadcrumb>
                <Link to={routes.keyIssuesIndex.path}>Key Issues</Link>&nbsp;&bull;&nbsp;
                <Link to={buildRoutePath(routes.keyIssuesIndex.path, {}, { category })}>{category}</Link>&nbsp;&bull;&nbsp;
                {title}
            </Breadcrumb>
            <div className={`key-issue-overview ${color}-bg`}>
                <div className="header">
                    <div className="container">
                        <div className="header-content">
                            <div className="title-wrapper">
                                <h1>{keyIssue?.title}</h1>
                                <div className="title-stroke">
                                    <MarkerStroke />
                                </div>
                            </div>
                            <div className="image">
                                <img src={primaryImage?.url} alt={primaryImage?.alt} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="content-bento">
                        <div className="content-left">
                            <div className="text-block">
                                <MarkdownWrapper content={textBlock} />
                            </div>
                        </div>
                        <div className="content-right">
                            {/* 3 Facts use map on keyIssueShort.contentBands then three facts components */}
                            {contentBands &&
                                <>
                                    {contentBands.map((contentBand, index) => (
                                        <ThreeFacts key={index} contentBand={contentBand} />
                                    ))}
                                </>
                            }
                        </div>
                    </div>
                    { metaracismQuote &&
                        <MetaracismQuote content={metaracismQuote} />
                    }
                    <div className="key-issue-more">
                        <div className="container">
                            <Link to={buildRoutePath(routes.keyIssueView.path, { keyIssueSlug: slug })} className="btn medium">Dig Deeper</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(KeyIssueOverview)
